/**
 * Потолок роста и светофор достижимости цели.
 *
 * Логика: у каждого рычага есть предел реалистичного прироста для ниши.
 * Одновременная работа по нескольким фронтам размывает внимание,
 * поэтому итог штрафуется коэффициентом совмещения.
 */

import { LEVER, LEVERS_BY_RANK } from './levers';
import { COMBINATION_FACTOR, getLeverCaps } from '../../../data/diagnostic/benchmarks';

/** Насколько рычаг весит при выборе главного: быстрый рычаг чуть предпочтительнее. */
const RANK_WEIGHT = { 1: 1, 2: 0.95, 3: 0.85, 4: 0.8 };

const TOTAL_LEVERS = 4;

/**
 * Коэффициент совмещения для n рычагов.
 * Один рычаг — штрафа нет, все четыре — полный штраф из бенчмарков.
 */
export const combinationFactorFor = (leverCount) => {
  if (leverCount <= 1) return 1;
  const steps = Math.min(leverCount, TOTAL_LEVERS) - 1;
  return Math.pow(COMBINATION_FACTOR, steps / (TOTAL_LEVERS - 1));
};

/** Пределы прироста по нише, отфильтрованные по доступным рычагам. */
export const getAvailableCaps = (nicheId, availableLevers) => {
  const caps = getLeverCaps(nicheId);
  return availableLevers.map((leverId) => ({ id: leverId, cap: caps[leverId] || 0 }));
};

/**
 * Максимальный множитель выручки для набора рычагов, с учётом штрафа за совмещение.
 */
export const maxMultiplierFor = (leverCaps) => {
  if (!leverCaps.length) return 1;
  const raw = leverCaps.reduce((acc, lever) => acc * (1 + lever.cap), 1);
  return raw * combinationFactorFor(leverCaps.length);
};

/**
 * Потолок выручки: сколько бизнес может дать при работе по всем доступным рычагам.
 */
export const calculateCeiling = (state, nicheId) => {
  const leverCaps = getAvailableCaps(nicheId, state.availableLevers);
  const multiplier = maxMultiplierFor(leverCaps);

  return {
    multiplier,
    revenue: state.revenue * multiplier,
    growthPercent: (multiplier - 1) * 100,
    leverCaps,
    combinationFactor: combinationFactorFor(leverCaps.length),
    /** Потолок каждого рычага по отдельности, в деньгах. */
    perLever: leverCaps.map((lever) => ({
      id: lever.id,
      cap: lever.cap,
      capPercent: lever.cap * 100,
      revenue: state.revenue * (1 + lever.cap),
      gain: state.revenue * lever.cap,
    })),
  };
};

/**
 * Главный рычаг: самый большой резерв с поправкой на скорость эффекта.
 * Это единственный рычаг, который показывается в бесплатной части.
 */
export const findPrimaryLever = (state, nicheId) => {
  const caps = getLeverCaps(nicheId);

  const ranked = LEVERS_BY_RANK.filter((lever) => state.availableLevers.includes(lever.id))
    .map((lever) => {
      const cap = caps[lever.id] || 0;
      return {
        lever,
        cap,
        capPercent: cap * 100,
        gain: state.revenue * cap,
        score: cap * (RANK_WEIGHT[lever.rank] || 0.8),
      };
    })
    .sort((a, b) => b.score - a.score);

  return ranked.length ? ranked[0] : null;
};

export const TRAFFIC_LIGHT = {
  GREEN: 'green',
  YELLOW: 'yellow',
  RED: 'red',
};

/**
 * Светофор достижимости цели.
 * Красный — не приговор, а честный сигнал пересмотреть цель или срок.
 */
export const evaluateTarget = (state, ceiling, targetRevenue) => {
  const target = Number(targetRevenue) || 0;

  if (target <= 0) {
    return { status: null, valid: false };
  }

  const requiredMultiplier = target / state.revenue;
  const ratio = requiredMultiplier / ceiling.multiplier;

  let status = TRAFFIC_LIGHT.GREEN;
  if (ratio > 1) {
    status = TRAFFIC_LIGHT.RED;
  } else if (ratio > 0.85) {
    status = TRAFFIC_LIGHT.YELLOW;
  }

  if (requiredMultiplier <= 1) {
    return {
      valid: true,
      status: TRAFFIC_LIGHT.GREEN,
      requiredMultiplier,
      requiredGrowthPercent: (requiredMultiplier - 1) * 100,
      ratio,
      title: 'Цель уже достигнута',
      text: 'Вы указали цель ниже текущей выручки. Поставьте цель выше — покажем, что нужно сделать.',
    };
  }

  const copy = {
    [TRAFFIC_LIGHT.GREEN]: {
      title: 'Цель достижима',
      text: 'Цель укладывается в возможности вашего бизнеса. Дальше вопрос только в том, за какой срок и какими действиями.',
    },
    [TRAFFIC_LIGHT.YELLOW]: {
      title: 'Цель на пределе',
      text: 'Цель на границе возможного. Достичь её можно, но придётся работать сразу по нескольким направлениям и без права на ошибку.',
    },
    [TRAFFIC_LIGHT.RED]: {
      title: 'Цель выше потолка',
      text: 'При текущей модели бизнеса эта цель недостижима за обычный срок. Это не значит, что вы плохо работаете — значит, нужно либо сдвинуть срок, либо менять саму модель.',
    },
  };

  return {
    valid: true,
    status,
    requiredMultiplier,
    requiredGrowthPercent: (requiredMultiplier - 1) * 100,
    ratio,
    gap: Math.max(0, target - ceiling.revenue),
    ...copy[status],
  };
};

export { LEVER };
